import { ROUTE_SUBSCRIPTION } from "./VMConstants";

export const SUBSCRIPTION_ACTION_SELECT_PLAN = "select_plan";
export const SUBSCRIPTION_ACTION_UPGRADE = "upgrade";

const formatPlanLabel = (value) => {
  if (!value) return "";
  const label = String(value).trim();
  if (!label) return "";
  return label.charAt(0).toUpperCase() + label.slice(1);
};

/** Dialog title + primary CTA from API `action` */
export const getSubscriptionGateDialogMeta = (action) => {
  if (action === SUBSCRIPTION_ACTION_SELECT_PLAN) {
    return {
      title: "Trial team limit reached",
      confirmLabel: "View plans",
      dismissLabel: "Not now",
    };
  }
  return {
    title: "Team limit reached",
    confirmLabel: "View upgrade options",
    dismissLabel: "Not now",
  };
};

/** Polished body copy for subscription gate dialogs */
export const formatSubscriptionGateMessage = ({
  message,
  action,
  plan,
  plan_tier,
  max_users,
} = {}) => {
  const tierLabel = formatPlanLabel(plan_tier || plan);
  const maxFromMessage = message?.match(/up to (\d+) users/i)?.[1];
  const maxUsers =
    max_users ?? (maxFromMessage ? Number(maxFromMessage) : null);

  if (action === SUBSCRIPTION_ACTION_SELECT_PLAN) {
    if (maxUsers != null) {
      return `Your trial includes up to ${maxUsers} team members, including the account owner. Choose a subscription plan to invite additional users.`;
    }
    return "You've reached the team size limit on your trial. Choose a subscription plan to add more team members.";
  }

  if (tierLabel && maxUsers != null) {
    return `Your ${tierLabel} plan supports up to ${maxUsers} team members, including the account owner. Upgrade to a higher plan to add more users to your team.`;
  }

  if (tierLabel) {
    return `You've reached the team size limit for your ${tierLabel} plan. Upgrade to add more team members.`;
  }

  if (message) {
    return message
      .replace(
        /\(including account owner\)/gi,
        "(including the account owner)"
      )
      .replace(/supports up to (\d+) users/gi, "supports up to $1 team members")
      .replace(
        /Upgrade your plan to add more users\.?/gi,
        "Upgrade to a higher plan to add more team members."
      );
  }

  return "You've reached your plan's team size limit. Upgrade to continue adding team members.";
};

/** Pricing / checkout route for trial cap and paid upgrade */
export const getPricingRouteForGate = () => `/${ROUTE_SUBSCRIPTION}`;

/** `GET checkTeamUserLimit` — can open add-team form */
export const isTeamAddAllowed = (limitResponse) =>
  limitResponse?.status_code === 200 && limitResponse.allowed === true;

/** Normalized gate payload from limit or add-team APIs */
export const getSubscriptionGateFromResponse = (response) => {
  if (!response) return null;

  if (response.allowed === true && response.status_code === 200) {
    return null;
  }

  const action = response.action;
  if (action === SUBSCRIPTION_ACTION_SELECT_PLAN || action === SUBSCRIPTION_ACTION_UPGRADE) {
    return {
      message: response.message,
      action,
      plan: response.plan,
      plan_tier: response.plan_tier,
      max_users: response.max_users ?? response.usage?.max_users ?? null,
    };
  }

  if (response.status_code === 403) {
    return {
      message: response.message,
      action: response.action || SUBSCRIPTION_ACTION_UPGRADE,
      plan: response.plan,
      plan_tier: response.plan_tier,
      max_users: response.max_users ?? response.usage?.max_users ?? null,
    };
  }

  if (response.status_code === 400 && response.action) {
    return {
      message: response.message,
      action: response.action,
      plan: response.plan,
      plan_tier: response.plan_tier,
      max_users: response.max_users ?? response.usage?.max_users ?? null,
    };
  }

  return null;
};

export const getTeamLimitCounts = (limitResponse) => {
  const current =
    limitResponse?.current_users ??
    limitResponse?.usage?.current_users ??
    null;
  const max =
    limitResponse?.max_users ??
    limitResponse?.usage?.max_users ??
    null;

  return { current, max };
};

export const isTrialSubscriptionPlan = (limitResponse) =>
  limitResponse?.plan === "trial" || limitResponse?.plan_tier === "trial";

export const getRemainingTeamSlots = (limitResponse) => {
  const { current, max } = getTeamLimitCounts(limitResponse);
  if (max == null || current == null) return null;
  return Math.max(0, max - current);
};

export const formatTrialTeamNotice = (limitResponse) => {
  const { current, max } = getTeamLimitCounts(limitResponse);
  const remaining = getRemainingTeamSlots(limitResponse);

  if (max == null) {
    return "You are on a trial plan. Team member additions are limited during the trial period.";
  }

  if (remaining === 0) {
    return `You are on a trial plan. You have reached the limit of ${max} team members (including the account owner). Subscribe to add more users.`;
  }

  const slotsLabel = remaining === 1 ? "1 slot" : `${remaining} slots`;
  const usedLabel = current != null ? `${current} of ${max} used` : `up to ${max} allowed`;

  return `You are on a trial plan. You can add ${slotsLabel} (${usedLabel}, including the account owner).`;
};

export const formatTeamUsageLabel = (limitResponse) => {
  if (!limitResponse || limitResponse.status_code !== 200) return "";

  const { current, max } = getTeamLimitCounts(limitResponse);

  if (current == null) return "";
  if (max == null) return `Users: ${current}`;
  return `Users: ${current} / ${max}`;
};
